import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import OutlinedInput from "@mui/material/OutlinedInput";
import IconButton from "@mui/material/IconButton";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { toast } from "react-toastify";
import ConfirmDelete from "../../components/ConfirmDelete";
import QuillEditor from "../../components/QuillEditor";
import api from "../../utils/api";

const languages = ["en", "hy", "ru"];
const languageLabels = { en: "English", hy: "Armenian", ru: "Russian" };

const storageBase = () =>
  (process.env.REACT_APP_API_BASE_URL || "").replace(/\/api\/?$/, "") || "";

const emptyLangs = () => ({
  title_hy: "",
  title_en: "",
  title_ru: "",
  description_hy: "",
  description_en: "",
  description_ru: "",
});

const getError = (errors, field) => {
  const v = errors[field];
  if (Array.isArray(v) && v.length) return v[0];
  if (typeof v === "string") return v;
  return "";
};

export default function StudyAbroadPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingAccordionId, setSavingAccordionId] = useState(null);
  const [errors, setErrors] = useState({});
  const [title, setTitle] = useState({ title_hy: "", title_en: "", title_ru: "" });
  const [description, setDescription] = useState({
    description_hy: "",
    description_en: "",
    description_ru: "",
  });
  const [logos, setLogos] = useState([]);
  const [accordions, setAccordions] = useState([]);
  const descriptionEditorRefs = useRef({});
  const accordionEditorRefs = useRef({});

  const mapLogos = (list = []) =>
    list.map((s) => ({
      id: s.id,
      image: s.image,
      imageUrl: s.image ? `${storageBase()}/storage/${s.image}` : null,
    }));

  const mapAccordions = (list = []) =>
    list.map((a) => ({
      id: a.id,
      title_hy: a.title_hy ?? "",
      title_en: a.title_en ?? "",
      title_ru: a.title_ru ?? "",
      description_hy: a.description_hy ?? "",
      description_en: a.description_en ?? "",
      description_ru: a.description_ru ?? "",
    }));

  const loadPage = () => {
    return api.get("admin/content/study-abroad").then((res) => {
      const d = res.data?.data || {};
      const section = d.study_abroad || {};
      setTitle({
        title_hy: section.title_hy ?? "",
        title_en: section.title_en ?? "",
        title_ru: section.title_ru ?? "",
      });
      setDescription({
        description_hy: section.description_hy ?? "",
        description_en: section.description_en ?? "",
        description_ru: section.description_ru ?? "",
      });
      setLogos(mapLogos(d.logos || []));
      setAccordions(mapAccordions(d.accordions || []));
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadPage()
      .catch(() => {
        setLogos([]);
        setAccordions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTitleChange = (lang, value) => {
    setTitle((prev) => ({ ...prev, [`title_${lang}`]: value }));
    setErrors((prev) => ({ ...prev, [`title_${lang}`]: undefined }));
  };

  const handleDescriptionChange = (lang, value) => {
    setDescription((prev) => ({ ...prev, [`description_${lang}`]: value }));
    setErrors((prev) => ({ ...prev, [`description_${lang}`]: undefined }));
  };

  const onAddLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await api.post("admin/content/study-abroad/logos", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { id, image } = res.data || {};
      const imageUrl = image ? `${storageBase()}/storage/${image}` : null;
      setLogos((prev) => [...prev, { id, image, imageUrl }]);
      toast.success("University logo uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to upload logo.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const deleteLogo = async (idx) => {
    const logo = logos[idx];
    if (logo.id == null) {
      setLogos((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    try {
      await api.delete(`admin/content/study-abroad/logos/${logo.id}`);
      setLogos((prev) => prev.filter((_, i) => i !== idx));
      toast.success("Logo removed.");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to delete logo.");
    }
  };

  const moveLogo = (idx, direction) => {
    setLogos((prev) => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const moveAccordion = (idx, direction) => {
    setAccordions((prev) => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const updateAccordionField = (id, field, value) => {
    setAccordions((prev) =>
      prev.map((a) => (a.id === id ? { ...a, [field]: value } : a))
    );
  };

  const getAccordionDescription = (id, lang) => {
    const ref = accordionEditorRefs.current[`${id}-${lang}`];
    if (ref && typeof ref.getContent === "function") {
      const content = ref.getContent();
      if (content !== undefined && content !== null) return content;
    }
    const item = accordions.find((a) => a.id === id);
    return item?.[`description_${lang}`] ?? "";
  };

  const addAccordion = async () => {
    try {
      const res = await api.post("admin/content/study-abroad/accordions", emptyLangs());
      const accordion = res.data?.data?.accordion;
      if (accordion) {
        setAccordions((prev) => [...prev, ...mapAccordions([accordion])]);
        toast.success("Accordion added.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to add accordion.");
    }
  };

  const saveAccordion = async (id) => {
    const item = accordions.find((a) => a.id === id);
    if (!item) return;
    setSavingAccordionId(id);
    try {
      await api.put(`admin/content/study-abroad/accordions/${id}`, {
        title_hy: item.title_hy,
        title_en: item.title_en,
        title_ru: item.title_ru,
        description_hy: getAccordionDescription(id, "hy"),
        description_en: getAccordionDescription(id, "en"),
        description_ru: getAccordionDescription(id, "ru"),
      });
      toast.success("Accordion saved.");
      await loadPage();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save accordion.");
    } finally {
      setSavingAccordionId(null);
    }
  };

  const deleteAccordion = async (id) => {
    try {
      await api.delete(`admin/content/study-abroad/accordions/${id}`);
      setAccordions((prev) => prev.filter((a) => a.id !== id));
      toast.success("Accordion removed.");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to delete accordion.");
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setErrors({});
    try {
      const getDesc = (lang) => {
        const ref = descriptionEditorRefs.current[`desc-${lang}`];
        if (ref && typeof ref.getContent === "function") {
          const content = ref.getContent();
          if (content !== undefined && content !== null) return content;
        }
        return description[`description_${lang}`] ?? "";
      };
      await api.put("admin/content/study-abroad", {
        title_hy: title.title_hy,
        title_en: title.title_en,
        title_ru: title.title_ru,
        description_hy: getDesc("hy"),
        description_en: getDesc("en"),
        description_ru: getDesc("ru"),
        logo_ids: logos.map((s) => s.id).filter(Boolean),
        accordion_ids: accordions.map((a) => a.id).filter(Boolean),
      });
      await loadPage();
      toast.success("Study Abroad page saved.");
    } catch (e) {
      if (e?.errors && typeof e.errors === "object") setErrors(e.errors);
      toast.error(
        e?.errors
          ? "Please fix the validation errors."
          : e?.response?.data?.message || "Failed to save Study Abroad page."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography
        component="h2"
        variant="h5"
        sx={{ mb: 1, borderBottom: "1px solid", borderColor: "divider", pb: 1 }}
      >
        Study Abroad (public)
      </Typography>
      <Typography color="text.secondary">
        Page title and intro text in 3 languages, university logos, and accordion sections.
      </Typography>

      <FormControl fullWidth>
        <FormLabel sx={{ display: "block", mb: 1 }}>Page title</FormLabel>
        {languages.map((lang) => (
          <Box key={lang} sx={{ mb: 1.5 }}>
            <FormLabel sx={{ display: "block", mb: 0.5, fontSize: "0.85rem" }}>
              {languageLabels[lang]}
            </FormLabel>
            <OutlinedInput
              fullWidth
              size="small"
              value={title[`title_${lang}`] || ""}
              onChange={(e) => handleTitleChange(lang, e.target.value)}
              placeholder="Study Abroad"
            />
            {getError(errors, `title_${lang}`) && (
              <FormHelperText error>{getError(errors, `title_${lang}`)}</FormHelperText>
            )}
          </Box>
        ))}
      </FormControl>

      <Box>
        <FormLabel sx={{ display: "block", mb: 1 }}>Intro text (rich text)</FormLabel>
        {languages.map((lang) => (
          <Box key={lang} sx={{ mb: 2 }}>
            <FormLabel sx={{ display: "block", mb: 0.5, fontSize: "0.85rem" }}>
              {languageLabels[lang]}
            </FormLabel>
            <QuillEditor
              key={`study-abroad-desc-${lang}`}
              ref={(r) => {
                descriptionEditorRefs.current[`desc-${lang}`] = r;
              }}
              value={description[`description_${lang}`] || ""}
              onChange={(html) => handleDescriptionChange(lang, html)}
              placeholder="Introduction..."
            />
            {getError(errors, `description_${lang}`) && (
              <FormHelperText error>{getError(errors, `description_${lang}`)}</FormHelperText>
            )}
          </Box>
        ))}
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          University logos
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
          {logos.map((logo, idx) => (
            <Box
              key={logo.id ?? `logo-${idx}`}
              sx={{
                position: "relative",
                width: 140,
                height: 90,
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {logo.imageUrl ? (
                <img
                  src={logo.imageUrl}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "contain", padding: 8 }}
                />
              ) : null}
              <Box sx={{ position: "absolute", top: 4, right: 4, display: "flex", gap: 0.5 }}>
                <IconButton
                  size="small"
                  disabled={idx === 0}
                  onClick={() => moveLogo(idx, -1)}
                  sx={{ bgcolor: "rgba(255,255,255,0.9)" }}
                >
                  <ArrowUpwardIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={idx === logos.length - 1}
                  onClick={() => moveLogo(idx, 1)}
                  sx={{ bgcolor: "rgba(255,255,255,0.9)" }}
                >
                  <ArrowDownwardIcon fontSize="inherit" />
                </IconButton>
                <ConfirmDelete
                  confirmText="Remove this logo?"
                  dialogTitle="Remove logo"
                  onConfirm={() => deleteLogo(idx)}
                >
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: "rgba(0,0,0,0.5)",
                      color: "white",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ConfirmDelete>
              </Box>
            </Box>
          ))}
          <Box
            component="label"
            sx={{
              width: 140,
              height: 90,
              borderRadius: 1,
              border: "2px dashed",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
            }}
          >
            <input type="file" accept="image/*" hidden onChange={onAddLogo} disabled={uploadingLogo} />
            {uploadingLogo ? <CircularProgress size={28} /> : <AddPhotoAlternateIcon color="action" />}
          </Box>
        </Box>
      </Box>

      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">Accordions</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addAccordion}>
            Add accordion
          </Button>
        </Box>
        {accordions.map((item, idx) => (
          <Accordion key={item.id} defaultExpanded={idx === 0} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ flex: 1 }}>
                {item.title_en || item.title_hy || item.title_ru || `Accordion #${idx + 1}`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {languages.map((lang) => (
                  <FormControl key={`title-${item.id}-${lang}`} fullWidth>
                    <FormLabel sx={{ mb: 0.5, fontSize: "0.85rem" }}>
                      Title ({languageLabels[lang]})
                    </FormLabel>
                    <OutlinedInput
                      size="small"
                      value={item[`title_${lang}`] || ""}
                      onChange={(e) => updateAccordionField(item.id, `title_${lang}`, e.target.value)}
                    />
                  </FormControl>
                ))}
                {languages.map((lang) => (
                  <Box key={`desc-${item.id}-${lang}`}>
                    <FormLabel sx={{ display: "block", mb: 0.5, fontSize: "0.85rem" }}>
                      Content ({languageLabels[lang]})
                    </FormLabel>
                    <QuillEditor
                      key={`accordion-${item.id}-${lang}`}
                      ref={(r) => {
                        accordionEditorRefs.current[`${item.id}-${lang}`] = r;
                      }}
                      value={item[`description_${lang}`] || ""}
                      onChange={(html) =>
                        updateAccordionField(item.id, `description_${lang}`, html)
                      }
                      placeholder="Accordion content..."
                    />
                  </Box>
                ))}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => saveAccordion(item.id)}
                    disabled={savingAccordionId === item.id}
                  >
                    {savingAccordionId === item.id ? <CircularProgress size={20} /> : "Save accordion"}
                  </Button>
                  <Button
                    size="small"
                    disabled={idx === 0}
                    onClick={() => moveAccordion(idx, -1)}
                  >
                    Move up
                  </Button>
                  <Button
                    size="small"
                    disabled={idx === accordions.length - 1}
                    onClick={() => moveAccordion(idx, 1)}
                  >
                    Move down
                  </Button>
                  <ConfirmDelete
                    confirmText="Delete this accordion?"
                    dialogTitle="Delete accordion"
                    onConfirm={() => deleteAccordion(item.id)}
                  >
                    <Button size="small" color="error">
                      Delete
                    </Button>
                  </ConfirmDelete>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={24} /> : "Save page & order"}
        </Button>
      </Box>
    </Box>
  );
}
